<template>
  <div>
    <div class="flex flex-col sm:flex-row items-start sm:items-center mb-4">
      <!-- 타이틀 영역 -->
      <div class="w-full sm:w-auto mr-auto">
        <h5 class="flex items-center"><i class="dot"></i>{{ $t("위험성평가") }}</h5>
      </div>

      <!-- 버튼 영역 -->
      <div v-if="!readonly && !isPrint" class="flex flex-wrap justify-start items-center mt-2 sm:mt-0 gap-2">
        <AIButton :label="$t('이미지로 AI 추천')" icon="ImageIcon" theme="outline" :fullWidth="false" :tooltip="$t('현장 사진을 업로드하면 AI가 위험요인을 찾아줍니다')" @click="handleImageAIRecommendation" />
        <AIButton
          :label="$t('주요 유해·위험요인 AI 추천')"
          icon="ZapIcon"
          theme="outline"
          :fullWidth="false"
          padding="8px 20px"
          :disabled="!aiRecommendationEnabled"
          :tooltip="$t('AI가 주요 유해·위험요인을 추천합니다')"
          @click="handleAIRecommendation"
        />
        <vs-button color="secondary" type="border" @click="handleAddRow">{{ $t("항목추가") }}</vs-button>
        <vs-button color="secondary" :disabled="checkData.length === 0" @click="handleRemoveRow">{{ $t("선택삭제") }}</vs-button>
      </div>
    </div>

    <div style="overflow-x: auto">
      <table v-if="isPrint">
        <colgroup>
          <col style="min-width: 160px" />
          <col style="min-width: 160px" />
          <col style="width: 300px; min-width: 300px" />
          <col />
          <col style="width: 120px; min-width: 120px" />
          <col style="width: 100px; min-width: 100px" />
          <col style="width: 100px; min-width: 100px" />
          <col style="width: 100px; min-width: 100px" />
          <col style="width: 300px; min-width: 300px" />
          <col style="min-width: 160px" />
          <col style="width: 130px; min-width: 94px" />
          <col style="width: 130px; min-width: 94px" />
        </colgroup>
        <thead>
          <tr>
            <th rowspan="3">
              <div class="w-full text-center">{{ $t("①공종분류") }}</div>
            </th>
            <th rowspan="3">
              <div class="w-full text-center">{{ $t("②단위작업") }}</div>
            </th>
            <th rowspan="3">
              <div class="w-full text-center">{{ $t("③위험요인") }}</div>
            </th>
            <th rowspan="3">
              <div class="w-full text-center">{{ $t("재해형태") }}</div>
            </th>
            <th colspan="4" class="text-center">{{ $t("④위험성 결정") }}</th>
            <th rowspan="3">
              <div class="w-full text-center">{{ $t("⑤위험방지대책") }}</div>
            </th>
            <th rowspan="3">
              <div class="w-full text-center">{{ $t("⑥개선조치 담당자") }}<br />{{ $t("조치예정일") }}</div>
            </th>
            <th rowspan="2" colspan="2">
              <div class="w-full text-center">{{ $t("➉이행확인") }}<br />{{ $t("조치한 경우 √") }}</div>
            </th>
          </tr>
          <tr>
            <th rowspan="2" class="text-center">{{ $t("산출근거") }}</th>
            <th colspan="3" class="text-center">{{ $t("위험도 등급") }}</th>
          </tr>
          <tr>
            <th class="text-center">{{ $t("상") }}</th>
            <th class="text-center">{{ $t("중") }}</th>
            <th class="text-center">{{ $t("하") }}</th>
            <th class="text-center">{{ $t("근로자대표") }}</th>
            <th class="text-center">{{ $t("관리감독자") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in list" :key="item.id">
            <td>
              <div class="print-input">{{ item.input1 }}</div>
            </td>
            <td>
              <div class="print-input">{{ item.input2 }}</div>
            </td>
            <td>
              <div class="print-textarea">{{ item.input3 }}</div>
            </td>
            <td>
              <div class="print-input">{{ $t(`${item.input4}`.replaceAll("직접입력_", "").replaceAll("직접 입력_", "")) }}</div>
            </td>
            <td>
              <div class="print-input score-cell">{{ scoreSummary(item) }}</div>
            </td>
            <td><PrintRadio :value="item.input5 === '상'" class="justify-center" /></td>
            <td><PrintRadio :value="item.input5 === '중'" class="justify-center" /></td>
            <td><PrintRadio :value="item.input5 === '하'" class="justify-center" /></td>
            <td>
              <div class="print-textarea">{{ item.input6 }}</div>
            </td>
            <td>
              <div class="print-input">{{ item.input7 }}</div>
              <div class="print-input">{{ item.input8 }}</div>
            </td>
            <td><PrintCheckbox label="" :value="item.input9" style="width: fit-content" class="mx-auto" /></td>
            <td><PrintCheckbox label="" :value="item.input10" style="width: fit-content" class="mx-auto" /></td>
          </tr>
        </tbody>
      </table>

      <table v-else>
        <colgroup>
          <col v-if="!readonly" width="36" />
          <col style="min-width: 160px" />
          <col style="min-width: 160px" />
          <col style="width: 320px; min-width: 320px" />
          <col />
          <col style="width: 150px; min-width: 150px" />
          <col style="width: 70px; min-width: 70px" />
          <col style="width: 70px; min-width: 70px" />
          <col style="width: 70px; min-width: 70px" />
          <col style="width: 320px; min-width: 320px" />
          <col style="min-width: 160px" />
          <col style="width: 130px; min-width: 94px" />
          <col style="width: 130px; min-width: 94px" />
        </colgroup>
        <thead>
          <tr>
            <th v-if="!readonly" rowspan="3">
              <vs-checkbox :value="checkData.length === list.length && 0 < list.length" class="justify-center mx-0" @click.native="onClickAllCheck" />
            </th>
            <th rowspan="3">
              <div class="w-full text-center">{{ $t("①공종분류") }}</div>
            </th>
            <th rowspan="3">
              <div class="w-full text-center">{{ $t("②단위작업") }}</div>
            </th>
            <th rowspan="3">
              <div class="w-full text-center">{{ $t("③위험요인") }}</div>
            </th>
            <th rowspan="3">
              <div class="w-full text-center">{{ $t("재해형태") }}</div>
            </th>
            <th colspan="4" class="text-center">{{ $t("④위험성 결정") }}</th>
            <th rowspan="3">
              <div class="w-full text-center">{{ $t("⑤위험방지대책") }}</div>
            </th>
            <th rowspan="3">
              <div class="w-full text-center">{{ $t("⑥개선조치 담당자") }}<br />({{ $t("조치예정일") }})</div>
            </th>
            <th rowspan="2" colspan="2">
              <div class="w-full text-center">{{ $t("➉이행확인") }}<br />({{ $t("조치한 경우 √") }})</div>
            </th>
          </tr>
          <tr>
            <th rowspan="2" class="text-center">
              <div class="flex items-center justify-center">
                {{ $t("AI 위험도 판정") }}
                <vx-tooltip :title="$t('위험도 등급 산정 기준')" :html="riskGuideHtml" style="margin-left: 4px; display: inline-flex; align-items: center" position="top" delay=".1s">
                  <feather-icon icon="AlertCircleIcon" style="cursor: pointer; width: 16px" />
                </vx-tooltip>
              </div>
            </th>
            <th colspan="3" class="text-center">{{ $t("위험도 등급") }}</th>
          </tr>
          <tr>
            <th class="text-center">{{ $t("상") }}</th>
            <th class="text-center">{{ $t("중") }}</th>
            <th class="text-center">{{ $t("하") }}</th>
            <th class="text-center">{{ $t("근로자대표") }}</th>
            <th class="text-center">{{ $t("관리감독자") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, itemIndex) in list" :key="item.id">
            <td v-if="!readonly" style="background: #f9f9f9"><vs-checkbox v-model="checkData" :vs-value="item.id" /></td>
            <td><ml-input :readonly="readonly" v-model="item.input1" class="w-full" :placeholder="$t('철근작업')" /></td>
            <td><ml-input :readonly="readonly" v-model="item.input2" class="w-full" :placeholder="$t('철근조립')" /></td>
            <td>
              <div v-if="!readonly" class="mb-2">
                <AIButton
                  :label="$t('AI 분석')"
                  icon="ZapIcon"
                  :disabled="!item.input3"
                  :loading="!!riskTypeAnalyzingState[item.id]"
                  :loading-text="$t('분석중...')"
                  :tooltip="$t('위험요인을 기반으로 재해형태를 AI가 자동 분석합니다')"
                  @click="handleAIRiskType(itemIndex)"
                />
              </div>
              <ml-textarea
                :readonly="readonly || !!riskTypeAnalyzingState[item.id]"
                rows="3"
                v-model="item.input3"
                :placeholder="$t('8층 기둥철근조립 중 틀비계 안전난간대 미 설치로 인한 추락위험')"
                class="w-full mb-0"
              />
            </td>
            <td>
              <disaster-class-selector class="disaster" :readonly="readonly" :disabled="!!riskTypeAnalyzingState[item.id]" v-model="item.input4" />
            </td>
            <td class="align-top">
              <div v-if="!readonly" class="mb-2">
                <AIButton
                  :label="$t('AI 판정')"
                  icon="TrendingUpIcon"
                  :disabled="!item.input3"
                  :loading="!!riskPointAnalyzingState[item.id]"
                  :loading-text="$t('분석중...')"
                  :tooltip="$t('AI가 가능성·중대성을 분석해 상/중/하 등급을 자동 판정합니다')"
                  @click="handleAIRiskPoint(itemIndex)"
                />
              </div>
              <a v-if="item.riskAnalysis && item.riskAnalysis.평가근거" class="analysis-link" @click="openAnalysis(item)">{{ $t("평가근거 보기") }}</a>
            </td>
            <td>
              <PrintRadio v-if="readonly" :value="item.input5 === '상'" class="justify-center" />
              <vs-radio v-else :vs-name="`risk_${item.id}`" vs-value="상" v-model="item.input5" />
            </td>
            <td>
              <PrintRadio v-if="readonly" :value="item.input5 === '중'" class="justify-center" />
              <vs-radio v-else :vs-name="`risk_${item.id}`" vs-value="중" v-model="item.input5" />
            </td>
            <td>
              <PrintRadio v-if="readonly" :value="item.input5 === '하'" class="justify-center" />
              <vs-radio v-else :vs-name="`risk_${item.id}`" vs-value="하" v-model="item.input5" />
            </td>
            <td>
              <div v-if="!readonly" class="mb-2">
                <AIButton
                  :label="$t('AI 추천')"
                  icon="ShieldIcon"
                  :disabled="!item.input3"
                  :loading="!!riskReductionsAnalyzingState[item.id]"
                  :loading-text="$t('분석중...')"
                  :tooltip="$t('현재 상황에 적합한 위험방지대책을 AI가 추천합니다')"
                  @click="handleAIRiskReductions(itemIndex)"
                />
              </div>
              <ml-textarea
                :readonly="readonly"
                rows="3"
                v-model="item.input6"
                :placeholder="$t('- 이동식비계 3 SET 반입 시 안전난간대 수량 확인(부족분 작업전 반입)  - 이동식비계 안전난간대 4개소 설치(상부 및 하부 난간대)')"
                class="w-full mb-0"
              />
            </td>
            <td>
              <ml-input :readonly="readonly" v-model="item.input7" class="w-full mb-2" :placeholder="$t('홍길동 반장')" />
              <date-selector :readonly="readonly" v-model="item.input8" class="w-full" :placeholder="$t('날짜선택')" />
            </td>
            <td>
              <PrintCheckbox v-if="readonly" label="" :value="item.input9" style="width: fit-content" class="mx-auto" />
              <vs-checkbox v-else v-model="item.input9" />
            </td>
            <td>
              <PrintCheckbox v-if="readonly" label="" :value="item.input10" style="width: fit-content" class="mx-auto" />
              <vs-checkbox v-else v-model="item.input10" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- AI 위험방지대책 추천 팝업 -->
    <popup-a-i-risk-reductions v-model="showRiskReductionsPopup" :request-data="riskReductionsRequestData" @selectComplete="handleRiskReductionsSelectComplete" />

    <!-- AI 위험도 평가근거 팝업 -->
    <popup-risk-analysis v-model="showRiskAnalysisPopup" :risk-analysis-data="currentRiskAnalysisData" :show-risk-group="true" />
  </div>
</template>

<script>
import DisasterClassSelector from "@/components/selector/DisasterClassSelector";
import DateSelector from "@/components/selector/DateSelector";
import PrintRadio from "@/components/print/PrintRadio.vue";
import PrintCheckbox from "@/components/print/PrintCheckbox.vue";
import AIButton from "@/components/AIButton.vue";
import PopupAIRiskReductions from "@/popup/PopupAIRiskReductions.vue";
import PopupRiskAnalysis from "@/popup/PopupRiskAnalysis.vue";
import { getRiskGroupByScore } from "@/util/RiskGroupUtil";

// AI 호출 실패 시 재시도까지의 대기 시간
const RETRY_DELAY_MS = 1000;

export default {
  components: {
    DisasterClassSelector,
    DateSelector,
    PrintRadio,
    PrintCheckbox,
    AIButton,
    PopupAIRiskReductions,
    PopupRiskAnalysis,
  },
  props: {
    readonly: { type: Boolean, default: false },
    isPrint: { type: Boolean, default: false },
    value: { type: Array, default: () => [] },
    aiRecommendationEnabled: { type: Boolean, default: true },
  },
  data() {
    return {
      checkData: [],
      list: this.value,

      // 행별 AI 로딩 상태
      riskTypeAnalyzingState: {},
      riskPointAnalyzingState: {},
      riskReductionsAnalyzingState: {},

      // AI 위험방지대책 팝업
      showRiskReductionsPopup: false,
      riskReductionsRequestData: {},
      currentRiskReductionsRowIndex: null,

      // AI 위험도 평가근거 팝업
      showRiskAnalysisPopup: false,
      currentRiskAnalysisData: null,
    };
  },
  computed: {
    riskGuideHtml() {
      return [
        `${this.$t("가능성")}(1~5) × ${this.$t("중대성")}(1~4) = ${this.$t("위험점수")}(1~20)`,
        "1 ~ 3 : 매우낮음 → 하",
        "4 ~ 6 : 낮음 → 하",
        "8 : 보통 → 중",
        "9 ~ 12 : 약간높음 → 중",
        "13 이상 : 높음 → 상",
      ].join("<br>");
    },
  },
  watch: {
    value: {
      handler(value) {
        this.list = value;
      },
    },
    list: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    // ── 위험도 등급 산정 ──────────────────────────────────────────
    scoreSummary(item) {
      if (!item.위험점수) return "";
      return `${item.가능성}×${item.중대성}=${item.위험점수}`;
    },
    // 점수를 반영해 위험도 등급(상/중/하)까지 한번에 적용
    applyRiskScore(rowIndex, possibility, severity) {
      const row = this.list[rowIndex];
      const score = Number(possibility) * Number(severity);
      this.$set(row, "가능성", Number(possibility));
      this.$set(row, "중대성", Number(severity));
      this.$set(row, "위험점수", score);
      this.$set(row, "input5", getRiskGroupByScore(score));
    },

    openAnalysis(item) {
      this.currentRiskAnalysisData = item.riskAnalysis;
      this.showRiskAnalysisPopup = true;
    },

    // AI 게이트웨이가 간헐적으로 502 를 반환하므로 일시적 실패는 한 번 더 시도한다.
    // 4xx 는 요청 자체가 잘못된 경우이므로 재시도하지 않는다.
    async dispatchWithRetry(action, params, retryCount = 1) {
      let lastError = null;

      for (let attempt = 0; attempt <= retryCount; attempt++) {
        try {
          const response = await this.$store.dispatch(action, params);
          if (response && response.success && response.data) return response;
          lastError = new Error("AI 분석 응답이 올바르지 않습니다.");
        } catch (error) {
          lastError = error;
          const status = error.response ? error.response.status : 0;
          if (status >= 400 && status < 500) break;
        }

        if (attempt < retryCount) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        }
      }

      throw lastError;
    },

    // ── 행 조작 ──────────────────────────────────────────────────
    onClickAllCheck() {
      if (this.checkData.length === this.list.length) this.checkData = [];
      else this.checkData = this.list.map((item) => item.id);
    },
    handleAddRow() {
      this.list.push(this.createRow());
    },
    handleRemoveRow() {
      this.list = this.list.filter((item) => !this.checkData.includes(item.id));
      this.checkData = [];
    },
    createRow(data = {}) {
      return {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        input1: "",
        input2: "",
        input3: "",
        input4: "",
        input5: "",
        input6: "",
        input7: "",
        input8: "",
        input9: false,
        input10: false,
        // AI 연동용 부가정보 (화면에는 노출하지 않고 후속 AI 호출에 사용)
        평가구분: "",
        유해위험요인: "",
        가능성: "",
        중대성: "",
        위험점수: "",
        ...data,
      };
    },

    // ── AI: 헤더 버튼 ────────────────────────────────────────────
    handleAIRecommendation() {
      this.$emit("ai-recommendation");
    },
    handleImageAIRecommendation() {
      this.$emit("image-ai-recommendation");
    },

    // ── AI: 재해형태 자동 분석 ───────────────────────────────────
    async handleAIRiskType(rowIndex) {
      const row = this.list[rowIndex];
      if (this.riskTypeAnalyzingState[row.id]) return;

      if (!row.input3) {
        this.notify("AI 분석 오류", "위험요인을 입력해주세요.", "danger");
        return;
      }

      try {
        this.$set(this.riskTypeAnalyzingState, row.id, true);

        const response = await this.dispatchWithRetry("miras/DEFINE_RISK_TYPE", { 세부작업명: row.input3 });

        this.$set(row, "평가구분", response.data.평가구분 || "");
        this.$set(row, "유해위험요인", response.data.유해위험요인 || "");
        this.$set(row, "input4", response.data.재해형태 || "");

        this.notify("AI 분석 완료", "재해형태가 성공적으로 분석되었습니다.", "success");
      } catch (error) {
        console.error("AI 분석 오류:", error);
        this.notify("AI 분석 오류", "분석 중 오류가 발생했습니다. 다시 시도해주세요.", "danger");
      } finally {
        this.$set(this.riskTypeAnalyzingState, row.id, false);
      }
    },

    // ── AI: 위험도(상/중/하) 판정 ────────────────────────────────
    async handleAIRiskPoint(rowIndex) {
      const row = this.list[rowIndex];
      if (this.riskPointAnalyzingState[row.id]) return;

      if (!row.input3) {
        this.notify("AI 위험도 분석 오류", "위험요인을 입력해주세요.", "danger");
        return;
      }

      try {
        this.$set(this.riskPointAnalyzingState, row.id, true);

        const params = {
          평가구분: row.평가구분 || "",
          유해위험요인: row.유해위험요인 || "",
          설명: row.input3, // 필수
          재해형태: (row.input4 || "").replace("직접입력_", ""),
          현재조치: row.input6 || "",
        };

        const response = await this.dispatchWithRetry("miras/MEASURE_CURRENT_RISK_POINT", params);

        const possibility = Number(response.data.가능성);
        const severity = Number(response.data.중대성);

        if (!possibility || !severity) {
          throw new Error("AI가 가능성·중대성을 산출하지 못했습니다.");
        }

        // 가능성 × 중대성 → 점수 → 상/중/하 등급
        this.applyRiskScore(rowIndex, possibility, severity);

        this.$set(row, "riskAnalysis", {
          가능성: response.data.가능성 || "",
          중대성: response.data.중대성 || "",
          위험성: response.data.위험성 || "",
          관리기준: response.data.관리기준 || "",
          평가근거: response.data.평가근거 || null,
        });

        if (response.data.평가근거) {
          this.currentRiskAnalysisData = row.riskAnalysis;
          this.showRiskAnalysisPopup = true;
        }

        this.notify("AI 위험도 분석 완료", `위험도 등급이 '${row.input5}'(으)로 판정되었습니다.`, "success");
      } catch (error) {
        console.error("AI 위험도 분석 오류:", error);
        this.notify("AI 위험도 분석 오류", error.message || "분석 중 오류가 발생했습니다. 다시 시도해주세요.", "danger");
      } finally {
        this.$set(this.riskPointAnalyzingState, row.id, false);
      }
    },

    // ── AI: 위험방지대책 추천 ────────────────────────────────────
    handleAIRiskReductions(rowIndex) {
      const row = this.list[rowIndex];

      if (!row.input3) {
        this.notify("AI 위험방지대책 추천 오류", "위험요인을 입력해주세요.", "danger");
        return;
      }

      this.riskReductionsRequestData = {
        평가구분: row.평가구분 || "",
        유해위험요인: row.유해위험요인 || "",
        설명: row.input3, // 필수
        재해형태: (row.input4 || "").replace("직접입력_", ""),
        현재조치: "",
        가능성: row.가능성 || "",
        중대성: row.중대성 || "",
      };

      this.currentRiskReductionsRowIndex = rowIndex;
      this.showRiskReductionsPopup = true;
    },

    handleRiskReductionsSelectComplete(selectedData) {
      if (this.currentRiskReductionsRowIndex === null || selectedData.length === 0) return;

      const row = this.list[this.currentRiskReductionsRowIndex];
      const formattedText = selectedData.map((item) => `- ${item.text}`).join("\n");
      const currentText = row.input6;

      this.$set(row, "input6", currentText && currentText.trim() !== "" ? `${currentText}\n${formattedText}` : formattedText);

      this.notify("AI 위험방지대책 추천 완료", `${selectedData.length}개의 위험방지대책이 추가되었습니다.`, "success");
      this.currentRiskReductionsRowIndex = null;
    },

    notify(title, text, color) {
      this.$vs.notify({ title, text, color, position: "top-right" });
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  table-layout: fixed;
  border-collapse: collapse;
  width: 100%;
  th,
  td {
    border: 1px solid #cdcdcd;
    padding: 5px;

    &::v-deep .con-vs-checkbox {
      justify-content: center !important;
      margin: 0;
      span {
        margin: 0;
      }
    }
    &::v-deep .con-vs-radio {
      width: 100%;
      justify-content: center !important;
    }
  }
  th {
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;

    background: #f9f9f9;
    padding: 7px;
  }
  td.align-top {
    vertical-align: top;
  }
}

.disaster::v-deep {
  .con-select {
    width: 100%;
  }
}

/* AI 위험도 평가근거 링크 */
.analysis-link {
  display: block;
  font-size: 12px;
  line-height: 17px;
  text-align: center;
  text-decoration: underline;
  cursor: pointer;
  color: #7367f0;
}

.score-cell {
  text-align: center;
  white-space: nowrap;
}
</style>
